import { PDFDocument, StandardFonts, rgb } from "pdf-lib";

const PAYMENT_TERMS_TEXT = "Payment due by {dueDate}.";
const PAYMENT_INSTRUCTIONS = [
  "Pay online by bank transfer",
  "Account Name: Connectify IT Services",
  "BSB: 067-105",
  "Account Number: 10617330",
];

const FOOTER_SYSTEM_TEXT = "This is a system-generated invoice.";
const FOOTER_THANK_YOU = "THANK YOU FOR YOUR BUSINESS!";

const wrapTextByWidth = (text, maxWidth, font, fontSize) => {
  const value = (text || "").trim();
  if (!value) return [""];

  const words = value.split(/\s+/);
  const lines = [];
  let currentLine = "";

  const pushWordChars = (word) => {
    let chunk = "";
    for (const ch of word) {
      const test = `${chunk}${ch}`;
      if (font.widthOfTextAtSize(test, fontSize) <= maxWidth || chunk.length === 0) {
        chunk = test;
      } else {
        lines.push(chunk);
        chunk = ch;
      }
    }
    if (chunk) lines.push(chunk);
  };

  for (const word of words) {
    const candidate = currentLine ? `${currentLine} ${word}` : word;
    if (font.widthOfTextAtSize(candidate, fontSize) <= maxWidth) {
      currentLine = candidate;
      continue;
    }

    if (currentLine) {
      lines.push(currentLine);
      currentLine = "";
    }

    if (font.widthOfTextAtSize(word, fontSize) > maxWidth) {
      pushWordChars(word);
    } else {
      currentLine = word;
    }
  }

  if (currentLine) lines.push(currentLine);
  return lines.length ? lines : [""];
};

const calcSubtotal = (items) =>
  items.reduce(
    (sum, it) =>
      sum + (Number(it.qty) || 0) * (Number(it.rate) || 0) - (Number(it.discount) || 0),
    0
  );

export const calculateTotals = (items, taxPercent) => {
  const subtotal = calcSubtotal(items);
  const tax = subtotal * (taxPercent / 100);
  return {
    subtotal,
    tax,
    total: subtotal + tax,
  };
};

export const generateInvoicePdfBytes = async (invoice, templateBytes) => {
  const templatePdf = await PDFDocument.load(templateBytes);
  const pdfDoc = await PDFDocument.create();

  const font = await pdfDoc.embedFont(StandardFonts.Helvetica);
  const boldFont = await pdfDoc.embedFont(StandardFonts.HelveticaBold);

  const { subtotal, tax, total } = calculateTotals(invoice.items, invoice.taxPercent);

  const tableTop = (height, isFirstPage) => (isFirstPage ? height - 250 : height - 170);
  const tableBottom = 190;
  const descFontSize = 10;
  const descLineHeight = 12;
  const minRowHeight = 20;
  const rowTopPadding = 5;
  const rowBottomPadding = 5;
  const colDesc = 50;
  const colQty = 400;
  const colRate = 450;
  const colAmount = 530;
  const descMaxWidth = colQty - colDesc - 14;

  const preparedRows = invoice.items.map((item) => {
    const descLines = wrapTextByWidth(item.description || "", descMaxWidth, font, descFontSize);
    const rowHeight = Math.max(
      minRowHeight,
      rowTopPadding + descLines.length * descLineHeight + rowBottomPadding
    );
    const amount =
      (Number(item.qty) || 0) * (Number(item.rate) || 0) - (Number(item.discount) || 0);

    return { item, descLines, rowHeight, amount };
  });

  const templateHeight = templatePdf.getPage(0).getSize().height;
  const firstPageCapacity = tableTop(templateHeight, true) - tableBottom - 20;
  const continuedPageCapacity = tableTop(templateHeight, false) - tableBottom - 20;
  const pages = [];
  let currentPageRows = [];
  let usedHeight = 0;

  preparedRows.forEach((row) => {
    const currentCapacity = pages.length === 0 ? firstPageCapacity : continuedPageCapacity;
    if (currentPageRows.length > 0 && usedHeight + row.rowHeight > currentCapacity) {
      pages.push(currentPageRows);
      currentPageRows = [];
      usedHeight = 0;
    }
    currentPageRows.push(row);
    usedHeight += row.rowHeight;
  });

  if (currentPageRows.length || pages.length === 0) {
    pages.push(currentPageRows);
  }

  for (let p = 0; p < pages.length; p++) {
    const [page] = await pdfDoc.copyPages(templatePdf, [0]);
    pdfDoc.addPage(page);

    const { width, height } = page.getSize();

    if (p === 0) {
      const headerY = height - 150;
      page.drawText("Bill To:", {
        x: 50,
        y: headerY,
        size: 11,
        font: boldFont,
      });

      page.drawText(invoice.to.name, {
        x: 50,
        y: headerY - 15,
        size: 10,
        font,
      });

      (invoice.to.address || "").split("\n").forEach((line, i) => {
        page.drawText(line, {
          x: 50,
          y: headerY - 30 - i * 12,
          size: 9,
          font,
        });
      });

      page.drawText(`Invoice: ${invoice.invoiceNumber}`, {
        x: 400,
        y: headerY,
        size: 11,
        font: boldFont,
      });

      page.drawText(`Date: ${invoice.date}`, {
        x: 400,
        y: headerY - 15,
        size: 10,
        font,
      });

      if (invoice.dueDate) {
        page.drawText(`Due: ${invoice.dueDate}`, {
          x: 400,
          y: headerY - 30,
          size: 10,
          font,
        });
      }
    }

    const currentTableTop = tableTop(height, p === 0);
    const tableLeft = colDesc;
    const tableRight = 580;
    const tableHeaderLineY = currentTableTop - 5;
    const tableClearTop = currentTableTop + 10;
    const tableClearBottom = tableBottom - 10;

    page.drawRectangle({
      x: tableLeft - 2,
      y: tableClearBottom,
      width: tableRight - tableLeft + 4,
      height: tableClearTop - tableClearBottom,
      color: rgb(1, 1, 1),
    });

    page.drawText("Description", {
      x: colDesc,
      y: currentTableTop,
      size: 10,
      font: boldFont,
    });

    page.drawText("Qty", {
      x: colQty,
      y: currentTableTop,
      size: 10,
      font: boldFont,
    });

    page.drawText("Rate", {
      x: colRate,
      y: currentTableTop,
      size: 10,
      font: boldFont,
    });

    page.drawText("Amount", {
      x: colAmount,
      y: currentTableTop,
      size: 10,
      font: boldFont,
    });

    page.drawLine({
      start: { x: tableLeft, y: tableHeaderLineY },
      end: { x: tableRight, y: tableHeaderLineY },
      thickness: 1,
    });

    let currentY = tableHeaderLineY;

    pages[p].forEach((row) => {
      const rowTextY = currentY - rowTopPadding - descFontSize;

      row.descLines.forEach((line, lineIdx) => {
        page.drawText(line, {
          x: colDesc,
          y: rowTextY - lineIdx * descLineHeight,
          size: descFontSize,
          font,
        });
      });

      page.drawText(String(row.item.qty), {
        x: colQty,
        y: rowTextY,
        size: 10,
        font,
      });

      page.drawText(`$${Number(row.item.rate).toFixed(2)}`, {
        x: colRate,
        y: rowTextY,
        size: 10,
        font,
      });

      page.drawText(`$${row.amount.toFixed(2)}`, {
        x: colAmount,
        y: rowTextY,
        size: 10,
        font,
      });

      const rowBottomY = currentY - row.rowHeight;
      page.drawLine({
        start: { x: tableLeft, y: rowBottomY },
        end: { x: tableRight, y: rowBottomY },
        thickness: 0.5,
      });

      currentY = rowBottomY;
    });

    if (p === pages.length - 1) {
      const totalsY = currentY - 40;

      page.drawText(`Subtotal: ${subtotal.toFixed(2)} ${invoice.currency}`, {
        x: 400,
        y: totalsY,
        size: 11,
        font,
      });

      page.drawText(`GST (${invoice.taxPercent}%): ${tax.toFixed(2)} ${invoice.currency}`, {
        x: 400,
        y: totalsY - 20,
        size: 11,
        font,
      });

      page.drawText(`Total: ${total.toFixed(2)} ${invoice.currency}`, {
        x: 400,
        y: totalsY - 40,
        size: 12,
        font: boldFont,
      });

      const notesY = totalsY - 80;
      page.drawText("Notes:", {
        x: 50,
        y: notesY,
        size: 11,
        font: boldFont,
      });

      const terms = PAYMENT_TERMS_TEXT.replace("{dueDate}", invoice.dueDate || "the due date");

      [terms, "", ...PAYMENT_INSTRUCTIONS].forEach((line, i) => {
        page.drawText(line, {
          x: 50,
          y: notesY - 15 - i * 12,
          size: 10,
          font,
        });
      });

      const thankYouWidth = boldFont.widthOfTextAtSize(FOOTER_THANK_YOU, 14);
      page.drawText(FOOTER_THANK_YOU, {
        x: (width - thankYouWidth) / 2,
        y: 60,
        size: 14,
        font: boldFont,
      });

      const sysTextWidth = font.widthOfTextAtSize(FOOTER_SYSTEM_TEXT, 8);
      page.drawText(FOOTER_SYSTEM_TEXT, {
        x: (width - sysTextWidth) / 2,
        y: 30,
        size: 8,
        font,
        color: rgb(0.6, 0.6, 0.6),
      });
    }
  }

  return pdfDoc.save();
};
